import type {
	HumanMessageDisplayContext,
	HumanMessageDisplayContextReference,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { MAX_VISIBLE_SELECTED_WORK_ITEMS } from '../../common/constants/display-context';

const isUsableDisplayContextReference = (
	reference: HumanMessageDisplayContextReference,
): boolean => {
	switch (reference.type) {
		case 'BROWSER_URL':
			return reference.browser_url.length > 0;
		case 'SELECTED_WORK':
			return reference.selected_work.total_count > 0 && reference.selected_work.items.length > 0;
	}
};

const getEffectiveDisplayContextReference = (
	displayContext: HumanMessageDisplayContext | null | undefined,
): HumanMessageDisplayContextReference | undefined => {
	const references = displayContext?.references;
	if (!references) {
		return undefined;
	}

	return (
		references.find(
			(reference) =>
				reference.type === 'SELECTED_WORK' && isUsableDisplayContextReference(reference),
		) ?? references.find(isUsableDisplayContextReference)
	);
};

const getDisplayContextReferenceIdentity = (
	reference: HumanMessageDisplayContextReference,
): string => {
	switch (reference.type) {
		case 'BROWSER_URL':
			return `BROWSER_URL:${reference.browser_url}`;
		case 'SELECTED_WORK': {
			const { source_url: sourceUrl, total_count: totalCount, items } = reference.selected_work;
			return `SELECTED_WORK:${JSON.stringify({
				sourceUrl: sourceUrl ?? null,
				totalCount,
				items: items
					.slice(0, MAX_VISIBLE_SELECTED_WORK_ITEMS)
					.map(({ identifier, issue_key: issueKey }) => issueKey ?? identifier),
			})}`;
		}
	}
};

export const getEffectiveDisplayContext = (
	displayContext: HumanMessageDisplayContext | null | undefined,
): { reference: HumanMessageDisplayContextReference; identity: string } | undefined => {
	const reference = getEffectiveDisplayContextReference(displayContext);
	return reference
		? { reference, identity: getDisplayContextReferenceIdentity(reference) }
		: undefined;
};
